/**
 * Inline completion form — work-order section 10 while the ärende is open
 * (spec §2). Pure: values and rules in, patches and callbacks out. The page
 * owns the draft (`useCompletionDraft`) and performs the API call in
 * `onSubmit` after the confirm dialog.
 */
import React, { useState } from 'react';
import { Alert, Badge, Button, Form } from 'react-bootstrap';
import { CheckCircle, Eye } from 'lucide-react';
import { SectionTitle } from './SectionTitle';
import ConfirmationModal from '../../../../Components/ConfirmationModal';
import { WORK_ORDER_LABELS, workOrderSectionTestId } from '../../workOrderSections';
import {
    CompletionDraftValues,
    CompletionRules,
    CONDITION_MAX,
    CONDITION_MIN,
    estimateYearsLeft,
    validateCompletion,
    yearsLeftLabel,
} from './completionValidation';
import './completionForm.css';

const L = WORK_ORDER_LABELS;
const COMMENT_MAX_LENGTH = 2000;
/** Slider position shown before the user has set a value (badge stays "Ej angivet"). */
const UNTOUCHED_SLIDER_VALUE = 5;

export interface CompletionFormComponent {
    key: string;
    name: string;
    expectedLifetimeYears: number | null;
}

export interface CompletionFormProps {
    values: CompletionDraftValues;
    onChange: (patch: Partial<CompletionDraftValues>) => void;
    /** `isOpen` must be true to render; `afterPhotoCount` feeds the photo requirement. */
    rules: CompletionRules;
    /** Only used when `rules.requiresConditionUpdate`. */
    components: CompletionFormComponent[];
    /** Internal users only; the guest page passes false. */
    showCost: boolean;
    /** "Lägg till efterbild" — scrolls to the Efter-bilder group in Dokumentation. */
    /** "Visa" on a Skick block; omitted = no button. */
    onViewComponent?: (key: string) => void;
    /** Called after the confirm dialog; the page performs the API call. */
    onSubmit: () => Promise<void>;
    submitting?: boolean;
    error?: string | null;
    /** The "Sparas automatiskt" indicator, rendered next to the heading. */
    autosaveSlot?: React.ReactNode;
}

function formatKr(amount: number): string {
    return Math.round(amount).toLocaleString('sv-SE');
}

function parseNumberInput(raw: string): number | null {
    if (raw.trim() === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

export function CompletionForm({
    values,
    onChange,
    rules,
    components,
    showCost,
    onViewComponent,
    onSubmit,
    submitting = false,
    error = null,
    autosaveSlot,
}: CompletionFormProps) {
    const [showConfirm, setShowConfirm] = useState(false);

    if (!rules.isOpen) return null;

    // Every unmet requirement is listed under the (disabled) submit button until
    // it is met — same on iOS — so the user never has to guess what is missing.
    const errors = validateCompletion(values, rules);
    const canSubmit = errors.length === 0 && !submitting;

    const patch = (p: Partial<CompletionDraftValues>) => onChange(p);

    const patchCondition = (key: string, value: number | null, comment?: string) => {
        const current = values.conditions[key] ?? { value: null };
        patch({
            conditions: {
                ...values.conditions,
                [key]: {
                    value,
                    comment: comment ?? current.comment,
                },
            },
        });
    };

    const handleConfirm = async () => {
        setShowConfirm(false);
        await onSubmit();
    };

    const costSet = values.actualCost !== null && values.actualCost > 0;
    const investmentKr = costSet && values.actualCostInvestmentPercentage !== null
        ? (values.actualCost as number) * values.actualCostInvestmentPercentage / 100
        : null;

    return (
        <section className="wo-completion wo-card" data-testid={workOrderSectionTestId('completion')}>
            <SectionTitle icon={CheckCircle} title={L.sectionCompletion} extra={autosaveSlot} />

            {error && <Alert variant="danger">{error}</Alert>}

            {/* 1. Kommentar */}
            <Form.Group className="mb-3" controlId="wo-completion-comment">
                <Form.Label>{L.comment}{rules.requiresComment ? ' *' : ''}</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={4}
                    maxLength={COMMENT_MAX_LENGTH}
                    placeholder={L.commentPlaceholder}
                    value={values.comment}
                    onChange={e => patch({ comment: e.target.value })}
                    disabled={submitting}
                />
                <div className="wo-completion__counter small text-muted">
                    {values.comment.length}/{COMMENT_MAX_LENGTH}
                </div>
            </Form.Group>

            {/* 3. Skick per linked component */}
            {rules.requiresConditionUpdate && components.map(component => {
                const condition = values.conditions[component.key];
                const value = condition?.value ?? null;
                const yearsLeft = value !== null ? estimateYearsLeft(value, component.expectedLifetimeYears) : null;
                const sliderId = `wo-completion-condition-${component.key}`;
                return (
                    <div
                        key={component.key}
                        className="wo-completion__condition border rounded p-3 mb-3"
                        data-testid="wo-completion-condition"
                    >
                        <div className="wo-completion__condition-header mb-2">
                            <h6 className="wo-completion__condition-title">
                                <label htmlFor={sliderId}>{L.condition} — {component.name} *</label>
                            </h6>
                            <Badge bg={value !== null ? 'primary' : 'secondary'} className="wo-completion__condition-badge">
                                {value !== null ? value.toFixed(1) : L.conditionNotSet}
                            </Badge>
                        </div>
                        <div className="wo-condition-slider">
                            <div className="wo-condition-slider__track" aria-hidden="true" />
                            <Form.Range
                                id={sliderId}
                                min={CONDITION_MIN}
                                max={CONDITION_MAX}
                                step={0.1}
                                value={value ?? UNTOUCHED_SLIDER_VALUE}
                                onChange={e => patchCondition(component.key, Number(e.target.value))}
                                disabled={submitting}
                            />
                        </div>
                        <div className="wo-completion__scale small text-muted">{L.conditionScale}</div>
                        {yearsLeft !== null && (
                            <div className="small text-muted mt-1">{yearsLeftLabel(yearsLeft)}</div>
                        )}
                        {onViewComponent && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="wo-completion__view mt-2"
                                onClick={() => onViewComponent(component.key)}
                            >
                                <Eye size={16} className="me-1" />
                                {L.show}
                            </Button>
                        )}
                        <Form.Group className="mt-3" controlId={`wo-completion-condition-comment-${component.key}`}>
                            <Form.Label>{L.conditionComment}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={condition?.comment ?? ''}
                                onChange={e => patchCondition(component.key, value, e.target.value)}
                                disabled={submitting}
                            />
                        </Form.Group>
                    </div>
                );
            })}

            {/* 4. Faktisk kostnad — internal only */}
            {showCost && (
                <>
                    <Form.Group className="mb-3" controlId="wo-completion-actual-cost">
                        <Form.Label>{L.actualCostOptional}</Form.Label>
                        <div className="wo-completion__cost-row">
                            <Form.Control
                                type="number"
                                min={0}
                                step={1}
                                inputMode="numeric"
                                className="wo-completion__cost-input"
                                value={values.actualCost ?? ''}
                                onChange={e => patch({ actualCost: parseNumberInput(e.target.value) })}
                                disabled={submitting}
                            />
                            <span className="text-muted">kr</span>
                        </div>
                    </Form.Group>
                    {costSet && (
                        <Form.Group className="mb-3" controlId="wo-completion-investment-share">
                            <Form.Label>{L.investmentShare}</Form.Label>
                            <div className="wo-completion__cost-row">
                                <Form.Control
                                    type="number"
                                    min={0}
                                    max={100}
                                    step={1}
                                    inputMode="numeric"
                                    className="wo-completion__cost-input"
                                    value={values.actualCostInvestmentPercentage ?? ''}
                                    onChange={e => {
                                        const parsed = parseNumberInput(e.target.value);
                                        patch({
                                            actualCostInvestmentPercentage: parsed === null
                                                ? null
                                                : Math.min(100, Math.max(0, parsed)),
                                        });
                                    }}
                                    disabled={submitting}
                                />
                                <span className="text-muted">%</span>
                                {investmentKr !== null && (
                                    <span className="text-muted small">{L.investmentEquals(formatKr(investmentKr))}</span>
                                )}
                            </div>
                        </Form.Group>
                    )}
                </>
            )}

            {/* 5. Submit */}
            <Button
                variant="primary"
                className="wo-completion__submit"
                onClick={() => setShowConfirm(true)}
                disabled={!canSubmit}
            >
                {L.complete}
            </Button>
            {errors.length > 0 && (
                <ul className="wo-completion__errors small text-danger" data-testid="wo-completion-errors">
                    {errors.map(message => <li key={message}>{message}</li>)}
                </ul>
            )}

            <ConfirmationModal
                show={showConfirm}
                title={L.completeConfirmTitle}
                message={L.completeConfirmBody}
                confirmText={L.completeConfirmOk}
                cancelText={L.completeConfirmCancel}
                variant="primary"
                loading={submitting}
                onConfirm={() => { void handleConfirm(); }}
                onCancel={() => setShowConfirm(false)}
            />
        </section>
    );
}

export default CompletionForm;
